import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  HiOutlineShieldCheck,
  HiOutlineCurrencyDollar,
  HiOutlineCheckCircle,
  HiOutlineSparkles,
  HiOutlineCog,
  HiOutlineUserGroup,
  HiOutlineDocumentText,
  HiOutlineClipboardDocumentList,
  HiOutlineScale,
  HiOutlineQuestionMarkCircle,
  HiOutlineBuildingOffice2,
  // HiOutlineNoSymbol,
  HiOutlineArrowRight,
  HiOutlineBars3,
  HiOutlineXMark,
  HiOutlinePlus,
  HiOutlineMinus,
  HiOutlineSun,
  HiOutlineMoon,
} from "react-icons/hi2";
import logo from "../assets/logo.png"; // adjust path if needed
import "./Home.css";

const Home = ({ user }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  
  const [isDark, setIsDark] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });
  
  // Keep the body class in sync on first load
  useEffect(() => {
    document.body.classList.toggle("dark-mode", isDark);
  }, [isDark]); // run once on mount
  
  const toggleDarkMode = () => {
    const newMode = !isDark;
    setIsDark(newMode);
    localStorage.setItem("darkMode", newMode.toString());
    //document.body.classList.toggle("dark-mode", newMode);
  };

  const renderPrimaryCTA = (extraClass = "") => {
    if (user) {
      if (user.emailVerified) {
        return (
          <RouterLink to="/app" className={`btn btn-primary ${extraClass}`}>
            Start Classifying <HiOutlineArrowRight />
          </RouterLink>
        );
      }
      return (
        <RouterLink to="/verify-email" className={`btn btn-primary ${extraClass}`}>
          Verify email to continue
        </RouterLink>
      );
    }
    return (
      <RouterLink to="/register" className={`btn btn-primary ${extraClass}`}>
        Get Started <HiOutlineArrowRight />
      </RouterLink>
    );
  };

  const faqs = [
    {
      q: "How are classifications generated?",
      a: "You describe the goods — or upload PDFs, images or documents — and optionally provide material, intended use and a CAS number for chemicals. The AI analyses the content against the latest customs data and returns HS code, AHECC and statutory codes, duty rates, GST exemption, TCO suggestions and its reasoning.",
    },
    {
      q: "What happens if I reach my monthly classification limit?",
      a: "Standard tier includes 150 classifications per month and Professional includes 3,000. Your usage is tracked and visible in your account dashboard. You can upgrade between plans at any time if your volume grows.",
    },
    {
      q: "Do I need to be a licensed customs broker to use it?",
      a: "No. Customs Pro AI is built for importers, exporters and customs experts — intuitive enough for beginners, robust enough for pros. If you are new, the guided fields and plain-language explanations walk you through each classification.",
    },
    {
      q: "Can my team see a demonstration before subscribing?",
      a: "Yes. Contact our support team at support@customspro.ai to schedule a personalised demo, and ask about corporate volume discounts for larger teams.",
    },
    {
      q: "How is billing handled?",
      a: "All fees are in Australian dollars and are inclusive of GST. Payments are processed securely through Stripe. You can manage your subscription from inside your account at any time.",
    },
  ];

  return (
    <div className="home">
      {/* ===== NAVBAR ===== */}
      <header className="navbar">
        <div className="nav-inner">
          <RouterLink to="/" className="nav-brand">
            <img src={logo} alt="Customs Pro AI" className="nav-logo" />
            <span>Customs Pro AI</span>
          </RouterLink>

          <nav className={`nav-links ${mobileMenuOpen ? "open" : ""}`}>
            <a href="#why" onClick={() => setMobileMenuOpen(false)}>Why Customs Pro AI</a>
            <a href="#how" onClick={() => setMobileMenuOpen(false)}>How it works</a>
            <a href="#suite" onClick={() => setMobileMenuOpen(false)}>Reference suite</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
            {!user && (
              <RouterLink to="/login" className="nav-login" onClick={() => setMobileMenuOpen(false)}>
                Log in
              </RouterLink>
            )}
            <div className="nav-cta-mobile">{renderPrimaryCTA()}</div>
          </nav>

          <div className="nav-right">
            {!user && (
              <RouterLink to="/login" className="nav-login-desktop">
                Log in
              </RouterLink>
            )}
            {renderPrimaryCTA("nav-cta-desktop")}
            <button
              className="menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <HiOutlineXMark size={26} /> : <HiOutlineBars3 size={26} />}
            </button>
            <button
              className="theme-toggle"
              onClick={toggleDarkMode}
              aria-label="Toggle dark mode"
              title="Toggle dark mode"
            >
              {isDark ? <HiOutlineSun size={22} /> : <HiOutlineMoon size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="hero">
        <div className="hero-inner">
          <span className="eyebrow">Australian customs classification</span>
          <h1>
            Precision for experts.
            <br />
            <span className="highlight">Simplicity for everyone.</span>
          </h1>
          <p className="lede">
            Customs Pro AI blends advanced artificial intelligence with user-friendly design
            to tackle customs classification like never before — fast, accurate results that
            save you time, money, and headaches.
          </p>

          <div className="hero-ctas">
            {renderPrimaryCTA()}
            <a href="#how" className="btn btn-outline">
              See how it works
            </a>
          </div>

          <div className="trust-chips">
            <div className="chip">
              <HiOutlineShieldCheck />
              <span>Designed for the Australian market by Australian brokers</span>
            </div>
            <div className="chip">
              <HiOutlineCurrencyDollar />
              <span>Transparent AUD pricing, GST inclusive</span>
            </div>
            <div className="chip">
              <HiOutlineCheckCircle />
              <span>AI suggestions you review and confirm</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURE RIBBON ===== */}
      <section className="ribbon">
        <div className="ribbon-inner">
          <span className="ribbon-label">One platform covers</span>
          {[
            "Tariff classification",
            "AHECC",
            "TCO lookup",
            "GST exemptions",
            "Schedule 4",
            "Precedents",
            "Refund reasons",
            "Depot codes",
            "Prohibited imports",
          ].map((item) => (
            <span key={item} className="ribbon-item">
              <HiOutlineCheckCircle /> {item}
            </span>
          ))}
        </div>
      </section>

      {/* ===== WHY ===== */}
      <section className="section" id="why">
        <div className="section-inner">
          <div className="section-header">
            <p className="eyebrow">Why Customs Pro AI?</p>
            <h2>The classification workflow, rebuilt around accuracy</h2>
            <p className="section-lede">
              Four reasons brokers, importers and exporters keep their work in Customs Pro AI.
            </p>
          </div>

          <div className="why-grid">
            {[
              {
                icon: <HiOutlineSparkles size={26} />,
                num: "01",
                title: "AI-Powered Precision",
                desc: "Get accurate HS and AHECC codes in seconds. Describe your goods - or upload an image or other IDM - and receive structured code suggestions with duty rates and explanations you can act on.",
              },
              {
                icon: <HiOutlineCog size={26} />,
                num: "02",
                title: "Effortless Automation",
                desc: "Process PDFs, images and documents with ease. Automatic TCO suggestions and GST exemption run alongside every classification, so potential concessions surface before you lodge.",
              },
              {
                icon: <HiOutlineCurrencyDollar size={26} />,
                num: "03",
                title: "Affordable Plans",
                desc: "Standard: 150 classifications a month for $49. Professional: 3,000 classifications and premium features for $89. All fees in Australian dollars, GST inclusive. Discounts for annual subscription",
              },
              {
                icon: <HiOutlineUserGroup size={26} />,
                num: "04",
                title: "For All Levels",
                desc: "Built for importers, exporters and customs experts alike. Get guided structure and plain-language explanations; intuitive enough for beginners, robust enough for pros.",
              },
            ].map((item) => (
              <div key={item.num} className="why-card">
                <div className="why-card-header">
                  {item.icon}
                  <span>{item.num}</span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="section section-alt" id="how">
        <div className="section-inner">
          <div className="section-header">
            <p className="eyebrow">How it works</p>
            <h2>From goods description to decision-ready output</h2>
          </div>

          <div className="steps-grid">
            {[
              { step: "1", title: "Sign up or log in", desc: "Create an account and choose between Standard or Professional based on your needs.", badge: true },
              { step: "2", title: "Classify your goods", desc: "Add a description or upload PDFs, images or documents. Our AI will analyze the content to suggest accurate HS and AHECC codes." },
              { step: "3", title: "Review AI suggestions", desc: "Check the classifications and TCO suggestions. Confirm the results as needed for compliance." },
              { step: "4", title: "Export and integrate", desc: "Download results, save to History, and integrate with your existing systems for seamless workflow management." },
            ].map((item) => (
              <div key={item.step} className="step-card">
                <div className="step-num">{item.step}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>

                {item.badge && (
                <img
                  src={isDark ? "/freetrialdark.svg" : "/freetrial.svg"}
                  alt="14-day free trial"
                  className="step-trial-badge"
                />
              )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== AI DRAFTS / YOU DECIDE ===== */}
      <section className="section section-dark">
        <div className="section-inner dark-grid">
          <div>
            <p className="eyebrow light">AI-assisted, expert-confirmed</p>
            <h2>The AI drafts. You decide.</h2>
            <p className="section-lede light">
              Every classification comes back with its reasoning, ready for your professional judgement - and your records.
            </p>

            <div className="verify-points">
              {[
                { title: "Edit or confirm before you save", desc: "Check the AI-generated classifications and TCO suggestions, then edit or confirm as needed." },
                { title: "Use on the go", desc: "In the warehouse? Use Customs Pro Ai on your mobile device to take a photo of goods in-app and classify directly." },
                { title: "Audit-ready PDF reports", desc: "Export the codes, duty rate, GST treatment, TCO suggestions and the explanation behind them into an audit ready PDF file." },
                { title: "History that keeps track", desc: "Save classifications, revisit past decisions, and export the trail whenever you need it." },
              ].map((item, i) => (
                <div key={i} className="verify-point">
                  <HiOutlineCheckCircle size={22} />
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Realistic Classification Results card */}
          <div className="results-preview">
            <div className="results-card">
              <h3 className="results-title">Classification Results</h3>

              <div className="results-section">
                <h4>Imports</h4>
                <div className="results-grid">
                  <div className="result-field">
                    <label>HS Code</label>
                    <div className="result-value">0304.87.00 (100%)</div>
                  </div>
                  <div className="result-field">
                    <label>Stat Code</label>
                    <div className="result-value">37</div>
                  </div>
                  <div className="result-field">
                    <label>Unit</label>
                    <div className="result-value">kg</div>
                  </div>
                  <div className="result-field">
                    <label>Duty Rate</label>
                    <div className="result-value">Free</div>
                  </div>
                  <div className="result-field">
                    <label>GST Exemption</label>
                    <div className="result-value">FOOD</div>
                  </div>
                </div>
                <div className="result-field full">
                  <label>TCO Suggestions</label>
                  <div className="result-value muted">No TCOs applicable</div>
                </div>
              </div>

              <div className="results-section">
                <h4>Exports</h4>
                <div className="result-field">
                  <label>AHECC</label>
                  <div className="result-value">03048700</div>
                </div>
              </div>

              <div className="results-section">
                <label>Explanation</label>
                <div className="explanation-box">
                  The product "Frozen yellowfin tuna fillets for human consumption" is classified under Chapter 3 of the tariff. Specifically, it falls under heading 0304 for "FISH FILLETS AND OTHER FISH MEAT (WHETHER OR NOT MINCED), FRESH, CHILLED OR FROZEN." HS code 0304.87.00 is chosen as it directly specifies "Frozen fillets of other fish: Tunas (of the genus Thunnus)," which precisely matches "Yellowfin Tuna Fillets" (Thunnus albacares). The Stat Code 37 is the corresponding code for this classification.
                </div>
              </div>

              <div className="results-actions">
                <button className="btn btn-primary full">Save Classification</button>
                <button className="btn btn-outline full">Export as PDF</button>
                <button className="btn btn-outline full">Clear Data</button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* ===== REFERENCE SUITE ===== */}
      <section className="section" id="suite">
        <div className="section-inner">
          <div className="section-header">
            <p className="eyebrow">The reference suite</p>
            <h2>All relevant lookup tables, under one login.</h2>
            <p className="section-lede">
              All the data sources brokers need daily, in one location.
            </p>
          </div>

          <div className="suite-grid">
            {[
              { icon: <HiOutlineDocumentText size={24} />, title: "Tariff Viewer", desc: "Search the tariff by section, heading, classification or description. Includes chapter and section notes." },
              { icon: <HiOutlineClipboardDocumentList size={24} />, title: "AHECC Viewer", desc: "Look up Australian Harmonized Export Commodity Classification codes." },
              { icon: <HiOutlineScale size={24} />, title: "TCO Viewer", desc: "Filter and search for Tariff Concession Orders by classification, TCO or description." },
              { icon: <HiOutlineQuestionMarkCircle size={24} />, title: "GST Exemptions Viewer", desc: "Check GST exemption codes against your goods." },
              { icon: <HiOutlineShieldCheck size={24} />, title: "Schedule 4 Viewer", desc: "Browse Schedule 4 concession items and treatment codes." },
              { icon: <HiOutlineDocumentText size={24} />, title: "Precedent Viewer", desc: "Search Australian classification precedents by number and keyword." },
              { icon: <HiOutlineCurrencyDollar size={24} />, title: "Refund Reason Viewer", desc: "Filter ICS refund codes when preparing refund or drawback claims." },
              { icon: <HiOutlineBuildingOffice2 size={24} />, title: "Depot Code Viewers", desc: "Lists all Customs and quarantine depot codes with establishment names." },
            ].map((item, i) => (
              <div key={i} className="suite-card">
                <div className="suite-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== EXPERT CHAT ===== */}
      <section className="section section-alt" id="chat">
        <div className="section-inner">
          <div className="chat-section-grid">
            <div>
              <p className="eyebrow">Expert chat</p>
              <h2>Ask an Australian customs and quarantine expert</h2>
              <p className="section-lede" style={{ marginTop: "1rem" }}>
                Not sure where a rule sits? Chat directly with an AI expert grounded in Australian customs and biosecurity requirements — from valuation and Interpretive Rules to quarantine obligations under the Biosecurity Act 2015. Select your required field of expertise, and swap between them in-chat.
              </p>
              
            </div>

            <div className="chat-preview">
              <div className="chat-header">
                <div className="chat-avatar">CP</div>
                <div>
                  <strong>Customs Pro AI Chat</strong>
                  <span>Customs and biosecurity answers, on demand</span>
                </div>
              </div>
              <div className="chat-body">
                <div className="msg user">
                  Is powdered milk GST-free on import?
                </div>
                <div className="msg bot">
                Yes, imported powdered milk is GST-free. The applicable exemption is: * <strong>exemption_code</strong>: AB1 * <strong>description</strong>: Milk, skim milk, butter milk (whether liquid, powdered, concentrated or condensed); casein; whey, whey powder or paste * <strong>category</strong>: Food and Beverages * <strong>legislative_reference</strong>: Section 38-2.
                </div>
              </div>
              <div className="chat-chips">
                <span>GST Exemptions</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* ===== PRICING ===== */}
      <section className="section" id="pricing">
        <div className="section-inner">
          <div className="section-header">
            <p className="eyebrow">Affordable plans</p>
            <h2>Simple pricing in AUD, GST inclusive</h2>
            <p className="section-lede">Choose the plan that matches your monthly volume.</p>
          </div>

          <div className="pricing-grid">
            {/* Standard */}
            <div className="price-card">
              <h3>Standard</h3>
              <div className="price">
                $49<span>/month</span>
              </div>
              <p className="price-note">AUD, GST inclusive</p>
              <ul>
                {[
                  "150 AI classifications per month",
                  "HS and AHECC code suggestions with explanations",
                  "Automatic TCO and GST exemption checks",
                  "Document upload — PDFs, images and text",
                  "Expert chat for customs and biosecurity questions",
                  "Export to PDF",
                ].map((item) => (
                  <li key={item}>
                    <HiOutlineCheckCircle /> {item}
                  </li>
                ))}
              </ul>
              <RouterLink to={user ? "/app" : "/register"} className="btn btn-outline full">
                Choose Standard
              </RouterLink>
            </div>

            {/* Professional */}
            <div className="price-card featured">
              <div className="popular-tag">Most popular</div>
              <h3>Professional</h3>

              <div className="price-row">
                <div className="price">
                  $89<span>/month</span>
                </div>
                <img
                  src={isDark ? "/freetrialdark.svg" : "/freetrial.svg"}
                  alt="14-day free trial"
                  className="price-trial-badge"
                />
              </div>

              <p className="price-note">AUD, GST inclusive</p>
              <ul>
                {[
                  "3,000 classifications per month",
                  "Everything in Standard",
                  "Save and view classification history",
                  "All reference viewers with advanced filtering",
                  "Built for broker teams and high-volume importers",
                ].map((item) => (
                  <li key={item}>
                    <HiOutlineCheckCircle /> {item}
                  </li>
                ))}
              </ul>
              <RouterLink to={user ? "/app" : "/register"} className="btn btn-primary full">
                Choose Professional
              </RouterLink>
            </div>
          </div>

          <p className="pricing-note">
            Need higher volumes?{" "}
            <a href="mailto:support@customspro.ai">Contact support@customspro.ai</a> about corporate discounts or a demo.
          </p>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="section section-alt" id="faq">
        <div className="section-inner narrow">
          <div className="section-header">
            <p className="eyebrow">Questions, answered</p>
            <h2>What brokers and importers ask us</h2>
          </div>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className={`faq-item ${openFaq === index ? "open" : ""}`}>
                <button className="faq-question" onClick={() => toggleFaq(index)}>
                  <span>{faq.q}</span>
                  {openFaq === index ? <HiOutlineMinus size={20} /> : <HiOutlinePlus size={20} />}
                </button>
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="final-cta">
        <div className="final-inner">
          <h2>Streamline your customs workflow</h2>
          <p>
            Fast, accurate classifications with the reference data of a full brokerage desk —
            designed for the Australian market by Australian brokers.
          </p>
          <div className="final-buttons">
            {renderPrimaryCTA()}
            <a href="mailto:support@customspro.ai" className="btn btn-outline-white">
              Book a personalized demo
            </a>
          </div>
          <p className="final-note">From $49/month AUD, GST inclusive</p>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-logo-row">
                <img src={logo} alt="Customs Pro AI" />
                <span>Customs Pro AI</span>
              </div>
              <p>
                Australian customs classification, tariff lookup, prohibited imports, GST
                exemptions, and expert chat — for importers, exporters and brokers.
              </p>
            </div>

            <div>
              <h4>Contact us</h4>
              <p><span>Support:</span> <a href="mailto:support@customspro.ai">support@customspro.ai</a></p>
              <p><span>Feedback:</span> <a href="mailto:feedback@customspro.ai">feedback@customspro.ai</a></p>
              <p><span>Billing:</span> <a href="mailto:billing@customspro.ai">billing@customspro.ai</a></p>
            </div>

            <div>
              <h4>Explore</h4>
              <a href="#why">Why Customs Pro AI</a>
              <a href="#how">How it works</a>
              <a href="#suite">Reference suite</a>
              <a href="#pricing">Pricing</a>
              <a
                href="/privacy"
                onClick={(e) => {
                  e.preventDefault();
                  window.open("/privacy", "privacy", "width=595,height=800,scrollbars=yes,resizable=yes");
                }}
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                onClick={(e) => {
                  e.preventDefault();
                  window.open("/terms", "terms", "width=595,height=800,scrollbars=yes,resizable=yes");
                }}
              >
                Terms & Conditions
              </a>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
              Customs Pro AI provides AI-powered assistance for customs classification and related
              tasks. Outputs are generated by AI systems and may not always be perfect or up to
              date — exercise your own professional judgement when relying on results, and consult
              qualified professionals for critical applications. © Customs Pro AI.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;